import React, { useState, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { string, arrayOf, object, bool } from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";

import InitialLocationStep from "./shared/steps/InitialLocationStep";
import PickDateStep from "./shared/steps/PickDateStep";

import MyDetailsStep from "./shared/steps/MyDetailsStep";
import FinalStep from "./shared/steps/FinalStep";
import TestDriveProgressLayout from "./shared/TestDriveProgressLayout";
import SimpleButton from "@components/shared/SimpleButton";
import BookingMessage from "components/shared/BookingMessage";

import { useUserProfileSelector, useGitIdSelector } from "@redux/reducers/deal";
import { useGetTestDrivePointImage } from "@hooks/useGetTestDrivePointImage";
import { updateGitIdAction } from "@redux/actions/dealActions";
import { postDealAction } from "@redux/actions/dealActions";
import { ROUTES } from "@shared/routing/routes";
import { useRoutingUtils } from "@hooks/useRoutingUtils";
import { useGetBrandIdRedirect } from "@hooks/useGetBrandIdRedirect";
import { useTestDrivePointSelector } from "@redux/reducers/testDrive";
import { useTestDriveTypesSelector } from "@redux/reducers/appConfig";
import { color, fontSize, fontWeight } from "@components/shared/utils";

import {
  CTA_TD_TYPES,
  DEAL_TYPE,
  TEST_DRIVE_TYPE_ID,
  TEST_DRIVE_TYPES,
} from "@shared/constants";

const ProgressSteps = styled.div`
  padding-top: 2rem;
  overflow-x: hidden;
  min-width: 24rem;
`;

const OffsetWrapper = styled.div`
  margin-left: 2rem;
`;

const StyledSimpleButton = styled(SimpleButton)`
  color: ${color("black1")};
  font-weight: ${fontWeight("bold")};
  font-size: ${fontSize("lg")};
  letter-spacing: 1px;
`;

const LaMaisonProgress = ({
  affiliation,
  parkopolyAvailableSlots,
  testDriveLocation,
  testDriveSessionData,
  testDriveExclusionDates,
  isSSRRendered,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const routingUtils = useRoutingUtils();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const userProfile = useUserProfileSelector();
  const testDriveTypes = useTestDriveTypesSelector();
  const selectedTestDrive = useTestDrivePointSelector();
  const dispatch = useDispatch();
  const gitId = useGitIdSelector();
  const getBrandIdRedirect = useGetBrandIdRedirect();
  const getTestDrivePointImage = useGetTestDrivePointImage();

  const { selectedSlot: initialSelectedSlot } = testDriveSessionData ?? {};

  const testDriveType =
    testDriveTypes?.find(({ id }) => id === selectedTestDrive?.type) ?? null;

  const testDriveTypeKey = TEST_DRIVE_TYPES.find(
    ({ id }) => id === testDriveType?.id
  )?.key;

  const progressBarColor = testDriveType?.colorHexa
    ? `#${testDriveType.colorHexa}`
    : theme.colors.testDriveAccent;

  const [isPickDateComplete, setPickDateComplete] = useState(
    Boolean(initialSelectedSlot)
  );

  const [isMyDetailsStepComplete, setMyDetailsStepComplete] = useState(
    Object.entries(userProfile).length > 0
  );

  const [isFinalStepComplete, setIsFinalStepComplete] = useState(false);

  useEffect(() => {
    if (isPickDateComplete && !isFinalStepComplete && userProfile.customerId) {
      dispatch(
        postDealAction(CTA_TD_TYPES.CUSTOMER_COMPLETED, DEAL_TYPE.TEST_DRIVE)
      );
    }
  }, [isPickDateComplete, isFinalStepComplete, userProfile.customerId]);

  const handleGitId = generatedGitId =>
    dispatch(updateGitIdAction(generatedGitId));

  const onClickReturn = () => {
    return router.push(
      ROUTES.TEST_DRIVE_INDEX,
      routingUtils.getLocalizedPathname(ROUTES.TEST_DRIVE_INDEX, null, language)
    );
  };

  const getBrandIdRedirectParameters = testDriveType => {
    if (!testDriveType) {
      return {
        route: null,
        params: null,
      };
    }

    switch (testDriveType) {
      case TEST_DRIVE_TYPE_ID.LAMAISON:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_LA_MAISON,
          params: { testDrivePointSlug: selectedTestDrive.slug },
        };

      case TEST_DRIVE_TYPE_ID.STORE:
        return {
          route: ROUTES.TEST_DRIVE_DETAIL_ENSTORE,
          params: { testDrivePointSlug: selectedTestDrive.slug },
        };

      default:
        return {
          route: ROUTES.TEST_DRIVE_INDEX,
          params: null,
        };
    }
  };

  if (!selectedTestDrive) {
    return null;
  }

  const {
    name,
    geoLatitude,
    geoLongitude,
    type,
    storeType,
  } = selectedTestDrive;

  const markerImageUrl = getTestDrivePointImage(type, storeType);

  const brandIdParameters = getBrandIdRedirectParameters(testDriveType?.id);

  return (
    <TestDriveProgressLayout
      markerImageUrl={markerImageUrl}
      geoLongitude={geoLongitude}
      geoLatitude={geoLatitude}
    >
      <OffsetWrapper>
        <StyledSimpleButton withArrow onClick={onClickReturn}>
          {t("basket.back")}
        </StyledSimpleButton>
      </OffsetWrapper>
      {t("testDrive.bookingMessage.heading") &&
        t("testDrive.bookingMessage.subheading") && (
          <BookingMessage
            heading={t("testDrive.bookingMessage.heading")}
            subheading={t("testDrive.bookingMessage.subheading")}
          />
        )}
      <ProgressSteps>
        <InitialLocationStep
          progressBarColor={progressBarColor}
          selectionName={name}
        />
        <PickDateStep
          affiliation={affiliation}
          isFinalStepComplete={isFinalStepComplete}
          isStepComplete={isPickDateComplete}
          progressBarColor={progressBarColor}
          resetProgress={() => {
            setMyDetailsStepComplete(false);
          }}
          setIsStepComplete={setPickDateComplete}
          testDriveType={testDriveTypeKey}
          parkopolyAvailableSlots={parkopolyAvailableSlots}
          testDriveLocation={testDriveLocation}
          testDriveExclusionDates={testDriveExclusionDates}
          initialSelectedSlot={initialSelectedSlot}
          testDriveTypes={testDriveTypes}
          isSSRRendered={isSSRRendered}
        />

        <AnimatePresence>
          {isPickDateComplete && (
            <motion.div
              key="details-step"
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ delay: 0.9 }}
            >
              <MyDetailsStep
                affiliation={affiliation}
                isStepComplete={isMyDetailsStepComplete}
                isFinalStepComplete={isFinalStepComplete}
                progressBarColor={progressBarColor}
                handleGitId={handleGitId}
                setIsFinalStepComplete={setIsFinalStepComplete}
                setIsStepComplete={setMyDetailsStepComplete}
                testDriveType={testDriveTypeKey}
                returnRoute={getBrandIdRedirect(
                  brandIdParameters.route,
                  brandIdParameters.params
                )}
              />
            </motion.div>
          )}
          {isFinalStepComplete && (
            <motion.div
              key="final-step"
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ delay: 0.9 }}
            >
              <FinalStep
                affiliation={affiliation}
                gitId={gitId}
                progressBarColor={progressBarColor}
                testDriveType={testDriveTypeKey}
                isSSRRendered={isSSRRendered}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </ProgressSteps>
    </TestDriveProgressLayout>
  );
};

LaMaisonProgress.propTypes = {
  affiliation: string,
  parkopolyAvailableSlots: arrayOf(object),
  testDriveLocation: object,
  testDriveSessionData: object,
  isSSRRendered: bool,
  testDriveExclusionDates: arrayOf(string),
};

export default LaMaisonProgress;
